import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useScroll, useSpring } from "framer-motion";
import { SectionReveal, StaggerWords } from "../components/Motion";
import MethodThread, { MethodThreadMobile } from "../components/MethodThread";
import { useT } from "../i18n/LanguageContext";

export default function MethodPage() {
  const { t } = useT();
  const [activeIdx, setActiveIdx] = useState(0);
  const bandRefs = useRef([]);
  const movementsRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: movementsRef,
    offset: ["start 0.8", "end 0.55"],
  });
  const threadProgress = useSpring(scrollYProgress, { stiffness: 55, damping: 20, mass: 0.5 });

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length === 0) return;
        const top = visible.sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        setActiveIdx(Number(top.target.dataset.idx));
      },
      { threshold: [0.3, 0.5, 0.7], rootMargin: "-30% 0px -30% 0px" }
    );
    bandRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [t.manifesto.length]);

  const isSlate = (i) => i % 2 === 1;

  return (
    <div data-testid="method-page">
      {/* ── Opening + movements: one relative wrapper so the fashion figure
          spans the opening section and all five movement bands ────────── */}
      <div className="relative" ref={movementsRef}>
      {/* ── Opening section (ecru, no min-height) ───────────────────── */}}
      <section
        data-testid="method-opening"
        className="bg-[#EFECE6]"
        style={{
          paddingTop: "clamp(5rem, 10vw, 9rem)",
          paddingBottom: "clamp(2.5rem, 5vw, 4rem)",
        }}
      >
        <div className="relative z-10 max-w-[1600px] mx-auto px-5 md:px-10">
          <div className="grid grid-cols-12 gap-6">
            <div className="col-span-12 md:col-span-3">
              <p className="type-caption text-[#4c535a] mb-6 md:mb-0">{t.method.eyebrow}</p>
            </div>
            <div className="col-span-12 md:col-span-9">
              <h1
                data-testid="method-headline"
                className="font-serif text-[#4c535a]"
                style={{
                  fontSize: "clamp(2rem, 1.2rem + 3.2vw, 4.25rem)",
                  lineHeight: 1.08,
                  letterSpacing: "-0.02em",
                  maxWidth: "18ch",
                }}
              >
                <StaggerWords text={t.method.title_1} />
                <StaggerWords text={t.method.title_2} delay={0.15} />
                <StaggerWords text={t.method.title_3} delay={0.3} />
              </h1>
              <SectionReveal delay={0.1}>
                <p
                  data-testid="method-subline"
                  className="text-[#4c535a]/85"
                  style={{
                    fontSize: "clamp(1.125rem, 1rem + 0.5vw, 1.375rem)",
                    lineHeight: 1.5,
                    maxWidth: "40ch",
                    marginTop: "clamp(1.5rem, 3vw, 2.5rem)",
                  }}
                >
                  {t.method.subtitle}
                </p>
              </SectionReveal>
            </div>
          </div>
        </div>
      </section>

      {/* ── Movements ─────────────────────────────────────────────────
          Below 1200px: single column, no sidebar.
          1200px+ (xl): CSS grid 280px / 1fr, gap 4rem — the sticky index
          lives in its own column and can never overlap the main column. */}
      <div className="bg-[#EFECE6] xl:grid xl:grid-cols-[280px_1fr] xl:gap-16 xl:px-10 xl:max-w-[1600px] xl:mx-auto">
        {/* Sticky index rail — desktop (1200px+) only, sticky inside its grid column */}
        <aside data-testid="method-index" className="hidden xl:block">
          <div className="sticky top-32 py-20">
            <p className="type-caption mb-5 text-[#4c535a]/70">{t.method.aside_label}</p>
            <ul className="space-y-3 text-[#4c535a]">
              {t.manifesto.map((m, i) => (
                <li
                  key={m.n}
                  data-testid={`method-index-item-${m.n}`}
                  className={`flex items-baseline gap-4 transition-opacity duration-500 ${
                    activeIdx === i ? "opacity-100" : "opacity-40"
                  }`}
                >
                  <span className="font-serif text-base w-6 flex-none">{m.n}</span>
                  <span className="type-body">{m.title}</span>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        <div className="relative">
          {/* Single label above the content — mobile/tablet only (desktop shows it in the rail) */}
          <div className="px-5 md:px-10 pt-16 pb-2 xl:hidden">
            <p data-testid="method-label" className="type-caption text-[#4c535a]/70">
              {t.method.aside_label}
            </p>
            <MethodThreadMobile />
          </div>

          {/* ── Alternating movement bands ──────────────────────────── */}
          {t.manifesto.map((m, i) => {
            const slate = isSlate(i);
            return (
              <section
                key={m.n}
                ref={(el) => (bandRefs.current[i] = el)}
                data-idx={i}
                data-testid={`movement-${m.n}`}
                className={`min-h-screen flex items-start pt-20 md:pt-32 pb-24 md:pb-32 ${
                  slate ? "bg-[#4c535a] text-[#EFECE6]" : "bg-[#EFECE6] text-[#4c535a]"
                }`}
              >
                <div className="relative z-10 w-full px-5 md:px-10 xl:px-14">
                  <SectionReveal>
                    <div className="flex items-baseline gap-6 md:gap-10 mb-10 md:mb-12">
                      <span className={`type-caption flex-none ${slate ? "text-[#EFECE6]/70" : "text-[#4c535a]/70"}`}>
                        {m.n}
                      </span>
                      <h2
                        className="font-serif min-w-0"
                        style={{
                          fontSize: "clamp(2.5rem, 1.5rem + 4vw, 5.5rem)",
                          lineHeight: 1.05,
                          letterSpacing: "-0.02em",
                          overflowWrap: "break-word",
                        }}
                      >
                        {m.title}.
                      </h2>
                    </div>
                    <p className={`type-body max-w-[60ch] ${slate ? "text-[#EFECE6]/85" : "text-[#4c535a]/85"}`}>
                      {m.body}
                    </p>
                  </SectionReveal>
                </div>
              </section>
            );
          })}
        </div>
      </div>

        {/* One elongated fashion figure — hat → hem — drawn across the opening
            section and all five movements (desktop ≥1280px only, behind text) */}
        <MethodThread progress={threadProgress} />
      </div>

      {/* ── Final centred CTA (ecru) ─────────────────────────────────── */}
      <section className="bg-[#EFECE6] py-32 md:py-40">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 text-center">
          <Link
            to="/services#the-edit"
            data-testid="method-cta-edit"
            className="type-h1 text-[#4c535a] link-sweep inline-block"
          >
            {t.method.cta_1} {t.method.cta_2}
          </Link>
        </div>
      </section>
    </div>
  );
}
